import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  Compass,
  Heart,
  Wind,
  Eye,
  Flame,
  Globe2,
  MapPin,
  ArrowRight,
  Flower2,
} from "lucide-react";
import DharmaWheel from "../components/DharmaWheel";
import MeditationTimer from "../components/MeditationTimer";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

const NOBLE_TRUTHS = [
  { n: "I", title: "Dukkha", subtitle: "There is suffering",
    body: "Life as we usually live it contains anxiety, ageing, illness, loss and a quiet dissatisfaction. The Buddha asked us not to deny this, but to look at it honestly." },
  { n: "II", title: "Samudāya", subtitle: "Suffering has a cause",
    body: "Our pain is not random. It arises from craving, aversion and the illusion that we are separate, fixed selves clinging to things that change." },
  { n: "III", title: "Nirodha", subtitle: "Suffering can end",
    body: "The same mind that creates suffering can be trained to release it. Peace is not somewhere else, it is what remains when craving softens." },
  { n: "IV", title: "Magga", subtitle: "There is a path",
    body: "The Buddha left a practical map to that release: the Noble Eightfold Path. It is not belief, it is practice, day by day, lifetime by lifetime." },
];

const EIGHTFOLD = [
  ["Right Understanding", "See life as it is, change, interconnection, the workings of cause and effect."],
  ["Right Intention", "Mean well in everything you do, kindness, renunciation, non-harming."],
  ["Right Speech", "Speak truthfully, kindly, helpfully, never to wound."],
  ["Right Action", "Act in ways that protect life, dignity and trust."],
  ["Right Livelihood", "Earn a living through work that does not harm others."],
  ["Right Effort", "Cultivate wholesome states of mind; let the unwholesome quietly fall away."],
  ["Right Mindfulness", "Stay present, to body, feeling, thought and the world around you."],
  ["Right Concentration", "Train the mind to settle, deepen, and see clearly."],
];

const MEDITATIONS = [
  {
    icon: Wind,
    name: "Samatha",
    pali: "Calm Abiding",
    color: "#1B6EA1",
    summary:
      "The first great river of Buddhist meditation. By gently resting the attention on a single object, most often the breath, the busy mind begins to settle, like muddy water becoming clear.",
    howto: [
      "Sit comfortably. Spine upright but not rigid.",
      "Lower or close the eyes. Soften the jaw and shoulders.",
      "Notice the natural breath at the nostrils or in the belly. Do not force it.",
      "When the mind wanders, and it will, gently return to the breath. No telling-off.",
      "Begin with 10 minutes daily. Build gradually to 25 or 40.",
    ],
    outcome:
      "Samatha steadies the mind. With practice you sleep more deeply, react less sharply, and discover a quiet inner ground that is not disturbed by every passing thought.",
  },
  {
    icon: Eye,
    name: "Vipassanā",
    pali: "Insight",
    color: "#001B3A",
    summary:
      "The second river. Where samatha calms, vipassanā sees. We watch experience itself, body, sensation, feeling, thought, noticing how everything arises and passes, with no fixed self at the centre.",
    howto: [
      "Establish a few minutes of breath calm first (samatha).",
      "Open the attention. Notice whatever is most prominent, a sound, a sensation, a thought.",
      "Label it gently: \"hearing… hearing…\" or \"thinking… thinking…\". Then return to the breath.",
      "Notice the rising and the falling of each experience.",
      "Practise without grasping pleasant moments or pushing away unpleasant ones.",
    ],
    outcome:
      "Vipassanā loosens the grip of identification. Anger comes, and is seen as a passing weather. Joy comes, and is enjoyed without clinging. Wisdom is born here.",
  },
  {
    icon: Heart,
    name: "Mettā",
    pali: "Loving-Kindness",
    color: "#E07A5F",
    summary:
      "The heart of the Buddha's emotional path. We deliberately cultivate goodwill, first for ourselves, then for those we love, then for strangers, then for those who have hurt us, then for all beings.",
    howto: [
      "Sit quietly. Rest the attention in the chest area.",
      "Silently offer phrases to yourself: \"May I be safe. May I be happy. May I be healthy. May I live with ease.\"",
      "After a few minutes, bring to mind someone you love. Offer the same phrases to them.",
      "Then a neutral person. Then a difficult person. Then all beings, everywhere.",
      "Notice resistance without judging it, just keep gently sending kindness.",
    ],
    outcome:
      "Mettā softens hardness. Over weeks and months it gently rewires the heart, loosening resentment and quietly building a generous default response to other people.",
  },
];

const OUTCOMES = [
  { icon: Flower2, title: "For the mind",
    body: "Reduced anxiety, sharper attention, better sleep, kinder self-talk. Modern psychology has rediscovered what Buddhism has known for 2,500 years." },
  { icon: Heart, title: "For the body",
    body: "Lower stress hormones, calmer nervous system, gentler blood pressure. Mindful breathing is now recommended by the NHS for many wellbeing concerns." },
  { icon: Sparkles, title: "For relationships",
    body: "More patience, less reactivity, kinder speech. Mettā practice in particular tends to soften long-held resentments without forcing forgiveness." },
  { icon: Compass, title: "For the world",
    body: "A non-violent ethic, a quiet refusal of greed, and a profound respect for every form of life, exactly what our hurting planet needs more of." },
];

const SCHOOLS = [
  {
    name: "Theravāda",
    where: "Sri Lanka, Thailand, Myanmar, Cambodia, Laos",
    body: "The 'Way of the Elders' — the oldest surviving school. Preserves the early teachings in the Pāli Canon, with a deep emphasis on monastic discipline and insight meditation.",
    accent: "#CFA052",
  },
  {
    name: "Mahāyāna",
    where: "China, Japan, Korea, Vietnam, Taiwan",
    body: "The 'Great Vehicle' — emphasises the bodhisattva ideal: choosing to remain in the world, again and again, for the liberation of all beings. Includes Zen, Pure Land and many others.",
    accent: "#1B6EA1",
  },
  {
    name: "Vajrayāna",
    where: "Tibet, Bhutan, Mongolia, Himalayan India",
    body: "The 'Diamond Vehicle' — combines Mahāyāna's compassion with elaborate meditative practices, mantras and visualisations under the guidance of a teacher (lama).",
    accent: "#5B8C5A",
  },
  {
    name: "Navayāna",
    where: "India, the United Kingdom, the global Ambedkarite diaspora",
    body: "The 'New Vehicle' — Babasaheb Dr. B.R. Ambedkar's modern revival, emphasising Buddhism as a path of social equality, dignity and rational compassion. Embraced by millions on 14 October 1956 at Deekshabhoomi, Nagpur.",
    accent: "#E07A5F",
  },
];

const UK_PRESENCE = [
  { city: "London", body: "Home to The Buddhist Society (founded 1924, Britain's oldest), the London Buddhist Centre, and many Theravāda, Tibetan and Zen centres." },
  { city: "Birmingham", body: "A vibrant Ambedkarite Buddhist community alongside Triratna and Theravāda centres serving the West Midlands." },
  { city: "Manchester & the North-West", body: "Stockport Meeting House (where ABGN-UK gathers), the Manchester Buddhist Centre, and a thriving multi-tradition presence." },
  { city: "Scotland & Wales", body: "Samye Ling in Eskdalemuir, Europe's first Tibetan monastery, and Lam Rim Bristol; meditation groups in Edinburgh, Glasgow, Cardiff." },
  { city: "Universities & Hospitals", body: "Buddhist chaplaincy is now established at Oxford, Cambridge, UCL and many NHS trusts, quietly serving patients of all faiths and none." },
  { city: "Online sangha", body: "The pandemic accelerated a global online sangha, meaning a Buddhist in any UK village can now sit, study and serve with thousands of others, every day of the week." },
];

export default function Dhamma() {
  return (
    <>
      {/* HERO */}
      <section className="relative bg-navy text-pearl overflow-hidden" data-testid="dhamma-hero">
        <div className="absolute -right-32 top-1/2 -translate-y-1/2 hidden md:block opacity-20">
          <DharmaWheel size={680} color="#CFA052" ringColor="#CFA052" />
        </div>
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(207,160,82,0.35), transparent 55%), radial-gradient(circle at 85% 80%, rgba(224,122,95,0.25), transparent 50%)",
          }}
        />
        <div className="relative section-container py-28 lg:py-36">
          <p className="eyebrow text-gold">The Path of the Buddha</p>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
            Dhamma, a path of <span className="italic text-gold">wisdom, compassion and freedom.</span>
          </h1>
          <p className="mt-8 text-lg text-pearl/80 max-w-2xl leading-relaxed">
            For 2,500 years, the Buddha's teachings have helped human beings face suffering with
            clarity and meet one another with kindness. They are not a creed to be believed, they
            are an invitation to look honestly, train gently, and live well.
          </p>
          <p className="mt-6 text-gold font-display italic text-2xl">
            Buddhaṃ saraṇaṃ gacchāmi · Dhammaṃ saraṇaṃ gacchāmi · Saṅghaṃ saraṇaṃ gacchāmi
          </p>
          <p className="mt-2 text-pearl/60 text-sm italic">
            I take refuge in the Buddha · in the Dhamma · in the Sangha
          </p>
        </div>
      </section>

      {/* THE BUDDHA */}
      <section className="py-20 bg-pearl" data-testid="dhamma-buddha">
        <div className="section-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow">Who was the Buddha?</p>
            <h2 className="mt-3 font-display text-4xl text-navy leading-tight">
              A prince who walked into the world to find truth.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-slate-700 leading-relaxed">
            <p>
              Around 2,500 years ago, in what is now Nepal and northern India, a young prince
              named <strong>Siddhartha Gautama</strong> left his palace to confront the suffering
              of the world. After six years of seeking, he sat under a fig tree and entered deep
              meditation, emerging as the <strong>Buddha</strong>, "the awakened one".
            </p>
            <p>
              He spent the next forty-five years walking the dusty roads of India, teaching
              farmers and kings, beggars and merchants, men and women, of every caste, in plain
              language. He left behind no temple, no successor and no infallible scripture, only
              a path and an invitation: <em>come and see for yourself</em>.
            </p>
            <p>
              That path, called the <strong>Dhamma</strong>, is what every Buddhist tradition has
              carried forward across two and a half millennia.
            </p>
          </div>
        </div>
      </section>

      {/* FOUR NOBLE TRUTHS */}
      <section className="py-24 bg-white border-y border-navy/10" data-testid="dhamma-four-truths">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow">The heart of the teaching</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">
              The Four Noble Truths.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              In his very first teaching, the Buddha shared these four observations, the
              foundation of every Buddhist tradition that followed.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
            {NOBLE_TRUTHS.map((t, i) => (
              <motion.div
                key={t.n}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeUp}
                custom={i}
                className="bg-pearl border border-navy/10 rounded-sm p-7 flex gap-5"
                data-testid={`noble-truth-${i + 1}`}
              >
                <div className="font-display text-5xl text-gold leading-none flex-shrink-0">
                  {t.n}
                </div>
                <div>
                  <p className="font-display text-2xl text-navy">{t.title}</p>
                  <p className="text-saffron text-xs uppercase tracking-[0.18em] mt-1">
                    {t.subtitle}
                  </p>
                  <p className="mt-3 text-slate-700 leading-relaxed text-[15px]">{t.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EIGHTFOLD PATH */}
      <section className="py-24 bg-pearl" data-testid="dhamma-eightfold">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <p className="eyebrow">The Buddha's path</p>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">
                The Noble Eightfold Path.
              </h2>
              <p className="mt-5 text-slate-700 leading-relaxed">
                The fourth Noble Truth opens into a path with eight gentle dimensions, like the
                eight spokes of a wheel. They are not eight steps to be climbed in order. They are
                eight habits of the heart to be cultivated together, all life long.
              </p>
              <div className="mt-8 inline-flex items-center justify-center">
                <DharmaWheel size={140} color="#001B3A" ringColor="#CFA052" />
              </div>
            </div>
            <div className="lg:col-span-7">
              <ol className="space-y-3">
                {EIGHTFOLD.map(([title, body], i) => (
                  <motion.li
                    key={title}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={fadeUp}
                    custom={i}
                    className="bg-white border border-navy/10 rounded-sm p-5 flex gap-4"
                  >
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-navy text-pearl flex items-center justify-center font-display text-base">
                      {i + 1}
                    </div>
                    <div>
                      <p className="font-display text-xl text-navy">{title}</p>
                      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</p>
                    </div>
                  </motion.li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* THREE JEWELS + FIVE PRECEPTS */}
      <section className="py-20 bg-white" data-testid="dhamma-foundations">
        <div className="section-container grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-pearl border border-navy/10 rounded-sm p-8">
            <Sparkles className="text-gold" size={28} strokeWidth={1.5} />
            <h3 className="mt-4 font-display text-3xl text-navy">The Three Jewels</h3>
            <p className="text-saffron text-xs uppercase tracking-[0.18em] mt-1">
              What every Buddhist takes refuge in
            </p>
            <ul className="mt-6 space-y-4 text-slate-700">
              <li><strong className="font-display text-navy text-lg">Buddha</strong>, the awakened teacher, and the awakened nature already present within each of us.</li>
              <li><strong className="font-display text-navy text-lg">Dhamma</strong>, the truth he uncovered: the way things actually are, and the path that leads to peace.</li>
              <li><strong className="font-display text-navy text-lg">Sangha</strong>, the community of fellow travellers, monks, nuns and lay practitioners walking together.</li>
            </ul>
          </div>
          <div className="bg-pearl border border-navy/10 rounded-sm p-8">
            <Flame className="text-saffron" size={28} strokeWidth={1.5} />
            <h3 className="mt-4 font-display text-3xl text-navy">The Five Precepts</h3>
            <p className="text-saffron text-xs uppercase tracking-[0.18em] mt-1">
              Promises every lay Buddhist quietly tries to keep
            </p>
            <ul className="mt-6 space-y-3 text-slate-700">
              <li>1. To refrain from harming living beings.</li>
              <li>2. To refrain from taking what is not freely given.</li>
              <li>3. To refrain from sexual misconduct.</li>
              <li>4. To refrain from false, harsh or careless speech.</li>
              <li>5. To refrain from intoxicants that cloud the mind.</li>
            </ul>
            <p className="mt-5 text-xs text-slate-500 italic">
              These are not commandments. They are training rules, kept gently, renewed daily.
            </p>
          </div>
        </div>
      </section>

      {/* MEDITATION DEEP DIVE */}
      <section className="py-24 bg-navy text-pearl" data-testid="dhamma-meditation">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow text-gold">Meditation in detail</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-tight">
              Three great rivers of practice.
            </h2>
            <p className="mt-5 text-pearl/80 leading-relaxed">
              Across all schools, three forms of meditation appear again and again. They support
              each other, most practitioners draw from all three across a lifetime.
            </p>
          </div>

          <div className="mt-14 space-y-10">
            {MEDITATIONS.map((m, i) => {
              const Icon = m.icon;
              return (
                <motion.article
                  key={m.name}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 bg-pearl/[0.04] border border-pearl/10 rounded-sm p-8 lg:p-10"
                  data-testid={`meditation-${m.name.toLowerCase()}`}
                >
                  <div className="lg:col-span-4">
                    <div
                      className="inline-flex items-center justify-center w-14 h-14 rounded-sm mb-5"
                      style={{ backgroundColor: `${m.color}26`, color: "#FDFBF7" }}
                    >
                      <Icon size={26} strokeWidth={1.5} />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">
                      {m.pali}
                    </p>
                    <h3 className="mt-2 font-display text-4xl">{m.name}</h3>
                    <p className="mt-4 text-pearl/75 leading-relaxed text-[15px]">{m.summary}</p>
                  </div>
                  <div className="lg:col-span-4">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">
                      How to practise
                    </p>
                    <ol className="mt-4 space-y-2.5 text-sm text-pearl/85 leading-relaxed">
                      {m.howto.map((step, idx) => (
                        <li key={idx} className="flex gap-3">
                          <span className="text-gold flex-shrink-0">{idx + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                  <div className="lg:col-span-4">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">
                      What it brings
                    </p>
                    <p className="mt-4 text-sm text-pearl/85 leading-relaxed italic font-display text-lg">
                      {m.outcome}
                    </p>
                  </div>
                </motion.article>
              );
            })}
          </div>

          <p className="mt-14 text-center text-pearl/60 italic font-display text-xl max-w-2xl mx-auto">
            "All that we are is the result of what we have thought." — The Dhammapada
          </p>
        </div>
      </section>

      {/* MEDITATION TIMER WIDGET */}
      <MeditationTimer />

      {/* OUTCOMES */}
      <section className="py-24 bg-pearl" data-testid="dhamma-outcomes">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow">What practice does</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">
              The fruits of the path.
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              The Buddha did not promise heaven. He promised something more grounded, a
              measurable reduction of suffering, and a measurable expansion of kindness.
              Two and a half millennia later, modern science is quietly confirming what
              practitioners have always known.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {OUTCOMES.map((o, i) => {
              const Icon = o.icon;
              return (
                <motion.div
                  key={o.title}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={fadeUp}
                  custom={i}
                  className="bg-white border border-navy/10 rounded-sm p-7"
                  data-testid={`outcome-${i}`}
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-saffron/15 text-saffron mb-4">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-2xl text-navy">{o.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed text-[14px]">{o.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SCHOOLS */}
      <section className="py-24 bg-white" data-testid="dhamma-schools">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow">One Dhamma · many rivers</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">
              The great schools of Buddhism.
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              As the Dhamma travelled out of India, it met new languages, new mountains and new
              hearts, and gently took new forms. All four major schools share the same Buddha,
              the same Four Noble Truths, the same path. The flavours differ; the medicine is one.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
            {SCHOOLS.map((s, i) => (
              <motion.div
                key={s.name}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeUp}
                custom={i}
                className="bg-pearl border border-navy/10 rounded-sm overflow-hidden"
                data-testid={`school-${s.name.toLowerCase()}`}
              >
                <div className="h-1.5" style={{ backgroundColor: s.accent }} />
                <div className="p-7">
                  <h3 className="font-display text-3xl text-navy">{s.name}</h3>
                  <p className="text-xs uppercase tracking-[0.18em] mt-1" style={{ color: s.accent }}>
                    {s.where}
                  </p>
                  <p className="mt-4 text-slate-700 leading-relaxed text-[15px]">{s.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WORLDWIDE */}
      <section className="py-24 bg-pearl" data-testid="dhamma-worldwide">
        <div className="section-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow">A path across the world</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">
              Buddhism around the world.
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              Today, an estimated <strong>500 million people</strong> across the world walk some
              form of the Buddha's path. From Tokyo to Toronto, from rural Sri Lanka to inner-city
              Manchester, the Dhamma has quietly become a truly global tradition, and one of the
              fastest-growing in the West.
            </p>
            <div className="mt-8 inline-flex items-center justify-center w-16 h-16 rounded-full bg-saffron/15 text-saffron">
              <Globe2 size={28} strokeWidth={1.5} />
            </div>
          </div>
          <div className="lg:col-span-7 space-y-4">
            <div className="bg-white border border-navy/10 rounded-sm p-6">
              <p className="text-[10px] uppercase tracking-[0.22em] text-saffron font-semibold">Asia · the heartland</p>
              <p className="mt-2 text-slate-700 leading-relaxed text-[15px]">
                Buddhism remains the majority faith in Thailand, Sri Lanka, Myanmar, Cambodia, Bhutan
                and parts of Japan, Vietnam, Korea and Mongolia. India, where it began, is now
                witnessing a powerful Ambedkarite revival.
              </p>
            </div>
            <div className="bg-white border border-navy/10 rounded-sm p-6">
              <p className="text-[10px] uppercase tracking-[0.22em] text-saffron font-semibold">The West · a quiet flowering</p>
              <p className="mt-2 text-slate-700 leading-relaxed text-[15px]">
                Buddhism arrived in Europe and North America in the late 19th century and has since
                become a respected presence in universities, hospitals, prisons and countless
                meditation centres. Mindfulness, its most modest export, now sits in the heart of
                NHS wellbeing programmes and global business culture.
              </p>
            </div>
            <div className="bg-white border border-navy/10 rounded-sm p-6">
              <p className="text-[10px] uppercase tracking-[0.22em] text-saffron font-semibold">Africa & Latin America · new growth</p>
              <p className="mt-2 text-slate-700 leading-relaxed text-[15px]">
                Smaller but vibrant communities have taken root from Cape Town to São Paulo to
                Mexico City, often beginning with a single dedicated teacher and growing one
                lay practitioner at a time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* UK */}
      <section className="py-24 bg-white" data-testid="dhamma-uk">
        <div className="section-container">
          <div className="max-w-3xl">
            <p className="eyebrow">Closer to home</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-navy leading-tight">
              Buddhism in Britain.
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              Britain has been a quiet companion to the Dhamma for over a century. The first
              English Buddhist organisation was founded in 1907; today, the UK is home to an
              estimated <strong>250,000 practising Buddhists</strong> from every tradition, and
              many more who quietly draw on the teachings without taking a label.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {UK_PRESENCE.map((p, i) => (
              <motion.div
                key={p.city}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeUp}
                custom={i}
                className="bg-pearl border border-navy/10 rounded-sm p-6"
                data-testid={`uk-${p.city.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                <MapPin className="text-gold" size={20} strokeWidth={1.6} />
                <h3 className="mt-3 font-display text-xl text-navy">{p.city}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed text-sm">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AMBEDKARITE CLOSING */}
      <section className="py-24 bg-navy text-pearl" data-testid="dhamma-ambedkarite">
        <div className="section-container max-w-4xl">
          <p className="eyebrow text-gold">Our place in this lineage</p>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-tight">
            The Ambedkarite path, Dhamma as dignity.
          </h2>
          <div className="mt-8 space-y-5 text-pearl/85 leading-relaxed text-[16px]">
            <p>
              On <strong className="text-gold">14 October 1956</strong>, Babasaheb Dr. B.R.
              Ambedkar, together with more than 500,000 of his followers, formally embraced the
              Buddha's path at Deekshabhoomi, Nagpur. It was the largest mass conversion in human
              history, and it gave the world a new river of the Dhamma: <strong>Navayāna</strong>,
              "the new vehicle".
            </p>
            <p>
              Babasaheb's Buddhism is not a departure from the Buddha, it is a return. He read
              the Pāli Canon for himself, and he found there a teacher who had stood, 2,500 years
              ago, against caste, against ritualism, against the chains placed on women, and for
              the radical equality of every human being.
            </p>
            <p>
              For ABGN-UK, the Dhamma is therefore not only a personal practice. It is also a
              <strong> civic ethic</strong>. We sit in meditation. We also stand against
              injustice. We cultivate compassion in the heart, and we demand fairness in the
              world. Both, together, are the path.
            </p>
          </div>
          <blockquote className="mt-10 border-l-4 border-gold pl-6 font-display italic text-2xl text-pearl">
            "I prefer Buddhism because it gives three principles in combination, which no other
            religion does, prajñā (understanding), karuṇā (love), and samatā (equality)."
            <span className="block mt-3 not-italic text-sm text-pearl/60">— Dr. B.R. Ambedkar</span>
          </blockquote>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-pearl" data-testid="dhamma-cta">
        <div className="section-container text-center max-w-3xl mx-auto">
          <p className="eyebrow">Begin your path</p>
          <h2 className="mt-3 font-display text-4xl text-navy leading-tight">
            Walk with us.
          </h2>
          <p className="mt-5 text-slate-700 leading-relaxed">
            Whether you are curious, a long-time practitioner, or simply seeking a calmer way to
            live, ABGN-UK welcomes you. Join a gathering, sit with the sangha, or simply read on.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/events"
              data-testid="dhamma-events-cta"
              className="inline-flex items-center gap-2 px-7 py-3 bg-navy text-pearl rounded-full font-medium hover:bg-navy-light"
            >
              See upcoming gatherings <ArrowRight size={16} />
            </Link>
            <Link
              to="/membership"
              data-testid="dhamma-membership-cta"
              className="inline-flex items-center gap-2 px-7 py-3 border border-navy/20 text-navy rounded-full font-medium hover:bg-navy/5"
            >
              Become a member
            </Link>
            <Link
              to="/reformers"
              data-testid="dhamma-reformers-cta"
              className="inline-flex items-center gap-2 px-7 py-3 text-navy hover:text-saffron font-medium"
            >
              Meet the reformers
            </Link>
          </div>
          <p className="mt-10 font-display italic text-gold text-xl">
            Sabbe sattā sukhitā hontu, May all beings be happy.
          </p>
        </div>
      </section>
    </>
  );
}
